import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LettersPullUp } from './LettersPullUp';
import FirestoreService from '../assets/js/firestore_instance';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, deleteDoc, updateDoc } from 'firebase/firestore';


const stripeDev = "https://buy.stripe.com/test_3cs3fWel94225jyfYZ"; //dev
const stripeProd = "https://buy.stripe.com/9AQ7vm9IH0NfaYw5kl";

const FadedWidget = ({ children }) => {
    return (
      <motion.div
        className="relative flex flex-col items-center my-2 dashboard-element-bg rounded-3xl shadow-md p-6 pt-7"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 2 }}
        style={{ filter: 'grayscale(100%)', opacity: 0.5, pointerEvents: 'none' }}
      >
        <h1 className="text-2xl font-bold mb-48">
            {children}
        </h1>
        <div className="absolute inset-0 bg-gray-700 bg-opacity-50 flex items-center justify-center rounded-3xl">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-20">
                <path fillRule="evenodd" d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3a3 3 0 0 0-3 3v6.75a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3v-6.75a3 3 0 0 0-3-3v-3c0-2.9-2.35-5.25-5.25-5.25Zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5Z" clipRule="evenodd" />
            </svg>
        </div>
      </motion.div>
    );
};

const GetStarted = () => {
  const [showOptions, setShowOptions] = useState(false);
  const [existingOrg, setExistingOrg] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  useEffect(() => {
      // Check for existing organization with "none" subscription
      const checkForExistingOrganization = async () => {
          const auth = getAuth();
          
          const unsubscribe = onAuthStateChanged(auth, async (user) => {
              if (user) {
                  try {
                      const db = FirestoreService.getFirestore();
                      const userDocRef = doc(db, "admin_users", user.uid);
                      const userDocSnap = await getDoc(userDocRef);
                      
                      if (userDocSnap.exists() && userDocSnap.data().organizationId && userDocSnap.data().organizationId.length > 0) {
                          const orgId = userDocSnap.data().organizationId[0];
                          const orgDocRef = doc(db, "organizations", orgId);
                          const orgDocSnap = await getDoc(orgDocRef);
                          
                          if (orgDocSnap.exists() && orgDocSnap.data().subscription === 'none') {
                              setExistingOrg({
                                  id: orgId,
                                  name: orgDocSnap.data().organizationName || 'your organization'
                              });
                          }
                      }
                  } catch (error) {
                      console.error("Error checking for existing organization:", error);
                  }
              }
          });
          
          return () => unsubscribe();
      };
      
      checkForExistingOrganization();
  }, []);
  
  const integratePCO = () => {
      window.location.href = "/auth";
  }

  const createOrganization = () => {
      window.location.href = "/create-organization";
  }
  
  const continueSetup = () => {
      window.location.href = stripeProd;
  }
  
  const startOver = async () => {
    setIsLoading(true); // Show loading indicator
    
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      console.error("User not authenticated");
      setIsLoading(false);
      return;
    }
    
    try {
      const db = FirestoreService.getFirestore();
      const orgId = existingOrg.id;
      
      // First, delete the organization document
      const orgDocRef = doc(db, "organizations", orgId);
      await deleteDoc(orgDocRef);
      console.log("Organization deleted successfully");
      
      // Next, update the admin_users document to remove this organization ID
      const userDocRef = doc(db, "admin_users", user.uid);
      const userDocSnap = await getDoc(userDocRef);
      
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        
        // Filter out the deleted org ID from the organizationId array
        if (userData.organizationId && Array.isArray(userData.organizationId)) {
          const updatedOrgIds = userData.organizationId.filter(id => id !== orgId);
          
          // Update the admin_users document
          await updateDoc(userDocRef, {
            organizationId: updatedOrgIds
          });
          console.log("Organization ID removed from user profile");
        }
      }
      
      // Reset state and redirect to create org page
      setExistingOrg(null);
      setShowOptions(false);
      window.location.href = "/dashboard";

    } catch (error) {
      console.error("Error deleting organization:", error);
      alert("There was an error deleting the organization. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
  <>
    <div className="relative flex flex-col items-center my-2 dashboard-element-bg rounded-3xl shadow-md w-full p-6 pt-10">
      <LettersPullUp text="Welcome to JoltChat" />
      <motion.p 
      className="text-lg mt-10 text-center px-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 2, delay: 1 }}>
          You're at the beginning of something great.
      </motion.p>
      
      <AnimatePresence>
        {isLoading ? (
          <motion.div
            key="loading"
            className="mt-16 my-8 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="loader"></div>
          </motion.div>
        ) : !showOptions ? (
          <motion.button 
            key="main-button"
            onClick={() => setShowOptions(true)}
            className="mt-16 my-8 primary-gradient text-white font-semibold text-2xl py-8 px-16 rounded-full shadow-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ 
              opacity: 0,
              y: -50,
              transition: { duration: 0.3 }
            }}
            transition={{ duration: 2, delay: 1 }}
          >
            Get Started
          </motion.button>
        ) : existingOrg ? (
          <motion.div
            key="existing-org-options"
            className="w-full flex flex-col md:flex-row justify-center items-center gap-6 mt-12 mb-8"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <motion.button 
              onClick={continueSetup}
              className="w-full md:w-auto primary-gradient text-white font-semibold text-xl py-6 px-8 rounded-full shadow-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Continue Setup for {existingOrg.name}
            </motion.button>
            
            <motion.button 
              onClick={startOver}
              className="w-full md:w-auto bg-gray-600 text-white font-semibold text-xl py-6 px-8 rounded-full shadow-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Start Over
            </motion.button>
          </motion.div>
        ) : (
          <motion.div
            key="option-buttons"
            className="w-full flex flex-col md:flex-row justify-center items-center gap-6 mt-12 mb-8"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <motion.button 
              onClick={createOrganization}
              className="w-full md:w-auto primary-gradient text-white font-semibold text-xl py-6 px-8 rounded-full shadow-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Start From Scratch
            </motion.button>
            
            <motion.button 
              onClick={integratePCO}
              className="w-full md:w-auto bg-blue-600 text-white font-semibold text-xl py-6 px-8 rounded-full shadow-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Integrate with Planning Center
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
    
    {/* Add some CSS for the loader */}
    <style jsx>{`
      .loader {
        border: 4px solid rgba(255, 255, 255, 0.3);
        border-radius: 50%;
        border-top: 4px solid #fff;
        width: 40px;
        height: 40px;
        animation: spin 1s linear infinite;
      }
      
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </>
  );
};

export default GetStarted;